import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';

import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import InnerTextInput from '@/components/Forms/InnerTextInput';
import TextInput from '@/components/Forms/TextInput';
import { ClientButton } from '@/components/UI/Button';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

// A user's marketing-SMS phone numbers + the opt-in to add one (#1799 / #1869 /
// #1676). Phones live in the dedicated `userPhones` table (split out of the now
// email-only contact slots) and exist for ONE purpose: consent-gated, occasional
// marketing SMS (scripts/sendTargetedSms.js). They are NOT a release-warning /
// coverage delivery channel.
//
// Adding a number is a real, affirmative opt-in: the user enters their own
// number, checks the (unchecked-by-default) marketing-consent box, and confirms a
// Twilio Verify code we text them. Existing numbers render read-only with a
// consent toggle (unchecking records an SMS-marketing opt-out on the row's
// `status`); the carrier-standard "reply STOP" path still works too. Keeping the
// opt-in language + disclosures visible documents consent for the Twilio A2P
// 10DLC registration. Layout mirrors the "Extra email recipients" control.
type UserPhone = {
  id: number;
  phone: string;
  verified: boolean;
  verifiedAt: string | null;
  addedAt: string | null;
  status: string | null;
};

const phonesRequest: RequestSchema<UserPhone[]> = {
  name: 'userPhonesOnFile',
  input: '/api/v1/user/phones/me',
  method: 'GET',
};

const removePhoneRequest: RequestSchema<UserPhone[], API.WithData<{ id: number }>> = {
  name: 'removeUserPhone',
  input: '/api/v1/user/phones/remove/me',
  method: 'POST',
};

const consentPhoneRequest: RequestSchema<UserPhone[], API.WithData<{ optOut: boolean }>> = {
  name: 'userPhoneConsent',
  input: '/api/v1/user/phones/consent/me',
  method: 'POST',
};

type AddResult = { status: string; phone: string; phones: UserPhone[] };

const addPhoneRequest: RequestSchema<
  AddResult,
  API.WithData<{ phone: string; acceptSms: boolean }>
> = {
  name: 'addUserPhone',
  input: '/api/v1/user/phones/add/me',
  method: 'POST',
};

const verifyPhoneRequest: RequestSchema<
  AddResult,
  API.WithData<{ phone: string; code: string }>
> = {
  name: 'verifyUserPhone',
  input: '/api/v1/user/phones/verify/me',
  method: 'POST',
};

// Pretty-print an E.164 US/CA number (+1XXXXXXXXXX) as +1 (XXX) XXX-XXXX; leave
// any other shape as-is.
function formatPhone(raw: string): string {
  const digits = `${raw || ''}`.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    const d = digits.slice(1);
    return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

const CONSENT_LABEL = (
  <>
    I agree to receive RAS marketing text messages at the number on file (not release alerts or
    account updates). Msg frequency varies. Msg and data rates may apply. Reply STOP to opt out or
    HELP for help. I have read the{' '}
    <a href="/terms" target="_blank" rel="noreferrer">
      RAS Terms of Service
    </a>{' '}
    and{' '}
    <a href="/privacy" target="_blank" rel="noreferrer">
      Privacy Policy
    </a>
    .
  </>
);

export default function LegacyPhonesOnFile() {
  // Best-effort lookup: a 404 / no numbers just means an empty list — suppress the
  // global error toast useRequest would otherwise pop on a non-2xx response.
  const phones = useRequest(phonesRequest, { manual: true, hideAlert: true });
  const removeReq = useRequest(removePhoneRequest, { manual: true });
  const consentReq = useRequest(consentPhoneRequest, { manual: true });
  const addReq = useRequest(addPhoneRequest, { manual: true });
  const verifyReq = useRequest(verifyPhoneRequest, { manual: true });

  // Add-a-number opt-in state. `pendingPhone` holds the number we sent a code to.
  const [adding, setAdding] = useState(false);
  const [phone, setPhone] = useState('');
  const [accept, setAccept] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  const [code, setCode] = useState('');

  useEffect(() => {
    phones.run().catch(() => undefined);
  }, []);

  const rows = phones.data ?? [];
  const busy = removeReq.loading || consentReq.loading;

  const remove = async (id: number) => {
    await removeReq.run({ data: { id } }, { throwOnRun: true }).catch(() => undefined);
    phones.run().catch(() => undefined);
  };

  // Consent is on when a number is verified and the user hasn't opted out.
  const optedOut = rows.some((row) => row.status === 'opted_out');
  const consented = rows.some((row) => row.verified) && !optedOut;

  const toggleConsent = async (nextChecked: boolean) => {
    await consentReq
      .run({ data: { optOut: !nextChecked } }, { throwOnRun: true })
      .catch(() => undefined);
    phones.run().catch(() => undefined);
  };

  const sendCode = async () => {
    if (!isValidPhoneNumber(phone) || !accept) {
      return;
    }
    const res = await addReq.run({ data: { phone, acceptSms: true } }, { throwOnRun: true });
    if (res) {
      setPendingPhone(res.phone || phone);
      setCode('');
    }
  };

  const confirmCode = async () => {
    if (!/^[0-9]{6}$/.test(code)) {
      return;
    }
    const res = await verifyReq.run({ data: { phone: pendingPhone, code } }, { throwOnRun: true });
    if (res?.status === 'approved') {
      setPendingPhone('');
      setPhone('');
      setAccept(false);
      setCode('');
      setAdding(false);
      phones.run().catch(() => undefined);
    }
  };

  const resetAdd = () => {
    setAdding(false);
    setPhone('');
    setAccept(false);
    setPendingPhone('');
    setCode('');
  };

  // The add form shows while actively adding, mid-verification, or when there is
  // no number on file yet; otherwise it collapses behind an "Add a number" link
  // (mirrors "Extra email recipients").
  const showAddForm = adding || !!pendingPhone || rows.length === 0;

  return (
    <div className="space-y-5">
      {rows.map((row) => (
        <div key={row.id} className="flex items-center gap-3">
          <TextInput
            readOnly
            value={formatPhone(row.phone)}
            aria-label="Marketing SMS number"
            className="max-w-sm cursor-default bg-zinc-50 font-mono text-zinc-600 dark:bg-white/5 dark:text-zinc-300"
          />
          <ClientButton
            type="button"
            variant="link"
            size="baseline"
            onClick={() => remove(row.id)}
            disabled={busy}
            loading={removeReq.loading}
            className="inline-flex items-center gap-1 p-0 font-medium text-zinc-500 no-underline hover:text-red-600 hover:no-underline dark:text-zinc-400 dark:hover:text-red-400"
          >
            <XMarkIcon className="size-4" aria-hidden="true" />
            Remove
          </ClientButton>
        </div>
      ))}

      {rows.some((row) => row.verified) && (
        <label className="flex max-w-md items-start space-x-2 text-xs text-zinc-500 dark:text-zinc-400">
          <input
            type="checkbox"
            checked={consented}
            disabled={busy}
            onChange={(e) => {
              void toggleConsent(e.target.checked);
            }}
            aria-label="Receive RAS marketing text messages"
            className="mt-0.5 size-4 shrink-0 rounded border-zinc-300 dark:border-zinc-600"
          />
          <span>{CONSENT_LABEL}</span>
        </label>
      )}

      {showAddForm &&
        (pendingPhone ? (
          <div className="max-w-md space-y-3">
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Enter the 6-digit code we texted to{' '}
              <span className="font-mono">{formatPhone(pendingPhone)}</span>.
            </p>
            <div className="flex items-end gap-3">
              <TextInput
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                maxLength={6}
                aria-label="Verification code"
                className="w-[7ch] text-center font-mono"
              />
              <FormSubmitButton
                type="button"
                inline
                onClick={confirmCode}
                disabled={verifyReq.loading || !/^[0-9]{6}$/.test(code)}
                loading={verifyReq.loading}
              >
                Verify
              </FormSubmitButton>
              <ClientButton
                type="button"
                variant="link"
                size="baseline"
                onClick={() => {
                  setPendingPhone('');
                  setCode('');
                }}
                disabled={verifyReq.loading}
              >
                Cancel
              </ClientButton>
            </div>
            {verifyReq.data && verifyReq.data.status !== 'approved' && (
              <p className="text-red-600 text-xs dark:text-red-400">
                That code did not match. Check it and try again.
              </p>
            )}
          </div>
        ) : (
          <div className="max-w-md space-y-3">
            <PhoneInput
              inputComponent={InnerTextInput}
              defaultCountry="US"
              value={phone}
              onChange={(what) => setPhone(what || '')}
              className="relative"
              autoComplete="tel"
              autoCapitalize="off"
              autoCorrect="off"
            />
            <label className="flex items-start space-x-2 text-xs text-zinc-500 dark:text-zinc-400">
              <input
                type="checkbox"
                checked={accept}
                onChange={(e) => setAccept(e.target.checked)}
                aria-label="Agree to receive RAS marketing text messages"
                className="mt-0.5 size-4 shrink-0 rounded border-zinc-300 dark:border-zinc-600"
              />
              <span>{CONSENT_LABEL}</span>
            </label>
            <div className="flex items-center gap-3">
              <FormSubmitButton
                type="button"
                inline
                onClick={sendCode}
                disabled={addReq.loading || !phone || !isValidPhoneNumber(phone) || !accept}
                loading={addReq.loading}
              >
                Send verification code
              </FormSubmitButton>
              {rows.length > 0 && (
                <ClientButton
                  type="button"
                  variant="link"
                  size="baseline"
                  onClick={resetAdd}
                  disabled={addReq.loading}
                >
                  Cancel
                </ClientButton>
              )}
            </div>
          </div>
        ))}

      {!showAddForm && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 font-semibold text-[#15734A] text-sm hover:underline dark:text-[#34B473]"
        >
          <PlusIcon className="size-4" aria-hidden="true" />
          Add {rows.length ? 'another' : 'a'} number
        </button>
      )}
    </div>
  );
}
